import { useCallback, useMemo } from 'react';
import { useTranslate } from '@agilepoint/wat-react';
import { IAuthenticationSettingsTextData } from '../queries';
import { AuthenticationProviderKeys } from '../types';

export const useAuthenticationProviders = (defaultConfig: string) => {
    const translate = useTranslate();
    return useMemo(() => [
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.agilepointIdTitle'),
            value: { iconName: 'ap-agilepoint-logo.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.AgilePointID.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.AgilePointID
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.activeDirectoryTitle'),
            value: { iconName: 'maa-windows-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.ActiveDirectory.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.ActiveDirectory
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.msaaDirectoryTitle'),
            value: { iconName: 'maa-ms-azure-active-directory-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.MSAzureActiveDirectory.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.MSAzureActiveDirectory
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.msaaDirectoryChinaTitle'),
            value: { iconName: 'maa-ms-azure-active-directory-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.MSAzureActiveDirectoryChina.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.MSAzureActiveDirectoryChina
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.oktaTitle'),
            value: { iconName: 'maa-okta-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.Okta.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.Okta
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.salesforceTitle'),
            value: { iconName: 'ap-salesforce-logo.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.Salesforce.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.Salesforce
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.pingFederate'),
            value: { iconName: 'maa-ping-federate-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.PingFederate.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.PingFederate
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.amazonCognito'),
            value: { iconName: 'maa-amazon-cognito-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.AmazonCognito.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.AmazonCognito
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.googleTitle'),
            value: { iconName: 'maa-google-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.GoogleConfiguration.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.GoogleConfiguration
        },
        {
            name: translate('modules.createNewApp.steps.authentication.authenticationProviderTitle.identityServerTitle'),
            value: { iconName: 'maa-identity-server-color.solid', markedAsDefault: defaultConfig === AuthenticationProviderKeys.IdentityServer.toLocaleLowerCase() },
            key: AuthenticationProviderKeys.IdentityServer
        }
    ], [defaultConfig, translate]);
};

export const useAuthenticationDefaultValues = (apiDefaultValues: Omit<IAuthenticationSettingsTextData, 'defaultConfig'>) => {
    const { serverConfiguration, ...restConfig } = apiDefaultValues;

    const getDefaultSelectedConfigs = useCallback((configs: Omit<typeof apiDefaultValues, 'serverConfiguration'>) => {
        const selectedConfigsList = [];
        for (const [key, value] of Object.entries(configs)) {
            if (value.isSelected) {
                selectedConfigsList.push(key);
            }
        }
        return selectedConfigsList;
    }, []);

    const selectedConfigs = useMemo(() => getDefaultSelectedConfigs(restConfig), [getDefaultSelectedConfigs, restConfig]);

    return {
        authentication: selectedConfigs,
        serverInstanceURL: serverConfiguration?.productionServerInstanceUrl,
        ...restConfig
    };
};
